import { createAsyncThunk } from '@reduxjs/toolkit';
import {
    fetchBenefitsForUserByTypeAndCategory,
    fetchAddedFamily,
    fetchModularSummaryForUser,
    getFamilyStructureDefinition
} from '../../../adapters';
import { IModularEnrolmentSummary, ISelectedAttributes } from './types';
import { BenefitPricingStructure, BenefitType, IAlacarteCategory } from '../PolicyListSlice/types';
import { sortFamilyMembers } from '../../../components/containers/ModularAddDependents/utils';
import { IFamilyStructure } from '../../../utils/types';

const MODULAR_ENROLMENT_ACTION_TYPES = {
    FETCH_MODULAR_PLANS: 'modularEnrolmentSlice/fetch-modular-plans',
    FETCH_MODULAR_SUMMARY: 'modularEnrolmentSlice/fetch-modular-summary',
    FETCH_MODULAR_ALA_CARTE_BENEFITS: 'modularEnrolmentSlice/fetch-modular-ala-carte-benefits',
    FETCH_MODULAR_ADD_ON_BENEFITS: 'modularEnrolmentSlice/fetch-modular-add-on-benefits',
    UPDATE_MODULAR_ALA_CARTE_BENEFITS: 'modularEnrolmentSlice/update-modular-ala-carte-benefits',
    FETCH_MODULAR_FAMILY: 'modularEnrolmentSlice/fetch-modular-family',
    FETCH_MODULAR_FAMILY_STRUCTURE: 'modularEnrolmentSlice/fetch-modular-family-structure',
    ADD_MEMBER: 'modularEnrolmentSlice/add-member',
    EDIT_MEMBER: 'modularEnrolmentSlice/edit-member',
    DELETE_MEMBER: 'modularEnrolmentSlice/delete-member'
};

export const fetchModularPlans = createAsyncThunk(
    MODULAR_ENROLMENT_ACTION_TYPES.FETCH_MODULAR_PLANS,
    async ({ userId, policyId }: { userId: string; policyId: string }) => {
        const response = await fetchBenefitsForUserByTypeAndCategory(userId, policyId, BenefitType.MODULAR);
        const plans = response?.benefits?.modular || [];
        const selectedPlanId = plans.find((p) => p.isSelected)?.id || plans.find((p) => p.isDefault)?.id;
        return { plans, selectedPlanId };
    }
);

export const fetchModularSummary = createAsyncThunk(
    MODULAR_ENROLMENT_ACTION_TYPES.FETCH_MODULAR_SUMMARY,
    async ({ userId, policyId }: { userId: string; policyId: string }) => {
        const response = await fetchModularSummaryForUser(userId, policyId);
        const summary: IModularEnrolmentSummary = {
            modular: response?.modular?.[0],
            alacarte: [...(response?.alacarte || []), ...(response.addOn || [])],
            priceSummary: response?.priceSummary ?? {}
        };
        return summary;
    }
);

export const fetchModularAlaCarteBenefits = createAsyncThunk(
    MODULAR_ENROLMENT_ACTION_TYPES.FETCH_MODULAR_ALA_CARTE_BENEFITS,
    async ({ userId, policyId, benefitId }: { userId: string; policyId: string; benefitId?: string }) => {
        const response = await fetchBenefitsForUserByTypeAndCategory(
            userId,
            policyId,
            BenefitType.ALA_CARTE,
            undefined,
            benefitId
        );
        const benefits = response?.benefits?.alacarte || [];
        const personalEmail = response?.email || '';
        const selectedBenefits = benefits?.flatMap((p) => {
            if (p.isSelected && p?.subCategories?.length === 0) {
                return [p.id];
            } else if (p?.subCategories?.length > 0) {
                return p?.subCategories?.filter((sub) => sub?.isSelected)?.map((sub) => sub?.id);
            }
            return [];
        });
        const userBenefits = benefits
            .filter((b) => b.pricingStructure === BenefitPricingStructure.PER_LIFE)
            .reduce((ub, b) => {
                const plans = b.subCategories.length ? b.subCategories : [b];
                plans.forEach((plan: IAlacarteCategory) => {
                    if (plan.isSelected && plan.selectedUserIds?.length) {
                        ub[plan.id] = plan.selectedUserIds;
                    }
                });
                return ub;
            }, {});
        const purchaseAttributes = benefits.reduce((attrs, benefit) => {
            // If benefit has purchaseAttributes and selectedPurchaseAttributes at root level
            if (benefit.purchaseAttributes) {
                if (benefit.subCategories.length) {
                    benefit.subCategories?.forEach((sub) => {
                        if (sub.selectedPurchaseAttributes) {
                            attrs[sub.id] = sub.selectedPurchaseAttributes.userSelection;
                        }
                    });
                } else if (benefit.selectedPurchaseAttributes) {
                    attrs[benefit.id] = benefit?.selectedPurchaseAttributes?.userSelection;
                }
            }
            return attrs;
        }, {} as ISelectedAttributes);
        return { benefits, selectedBenefits, personalEmail, userBenefits, purchaseAttributes };
    }
);

export const fetchModularAddOnBenefits = createAsyncThunk(
    MODULAR_ENROLMENT_ACTION_TYPES.FETCH_MODULAR_ADD_ON_BENEFITS,
    async ({ userId, policyId, benefitId }: { userId: string; policyId: string; benefitId?: string }) => {
        const response = await fetchBenefitsForUserByTypeAndCategory(
            userId,
            policyId,
            BenefitType.ADD_ON,
            undefined,
            benefitId
        );
        const addOns = response?.benefits?.addOn || [];
        const selectedAddOns: string[] = [];
        addOns?.forEach((p) => {
            if (p.isSelected && p?.subCategories?.length === 0) {
                selectedAddOns.push(p.id);
            } else if (p?.subCategories?.length > 0) {
                p?.subCategories?.filter((sub) => sub?.isSelected)?.forEach((sub) => selectedAddOns.push(sub?.id));
            }
        });
        let upgradedFamilyStructure: IFamilyStructure | undefined = undefined;
        if (selectedAddOns.length) {
            const response = await getFamilyStructureDefinition(benefitId ?? '', selectedAddOns);
            upgradedFamilyStructure = response;
        }
        return { addOns, selectedAddOns, upgradedFamilyStructure };
    }
);

export const fetchModularFamily = createAsyncThunk(
    MODULAR_ENROLMENT_ACTION_TYPES.FETCH_MODULAR_FAMILY,
    async ({ userId, policyId }: { userId: string; policyId: string }) => {
        const response = await fetchAddedFamily(userId, policyId);
        return sortFamilyMembers(response?.family || []);
    }
);

export const fetchModularFamilyStructure = createAsyncThunk(
    MODULAR_ENROLMENT_ACTION_TYPES.FETCH_MODULAR_FAMILY_STRUCTURE,
    async ({ modularPlanId }: { modularPlanId: string }) => {
        const structure = await getFamilyStructureDefinition(modularPlanId);
        return structure;
    }
);
